import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { ChannelParams } from "containers/TermFormContainer";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

// Lists term templates. Three modes:
//   - { beneficiaryId: null } (or no arg)              → generic typologies only (default)
//   - { beneficiaryId: X }                             → framework contracts of this beneficiary only
//   - { beneficiaryId: X, includeGeneric: true }       → framework contracts of X + generic typologies
export const fetchAllTermTemplates = createAsyncThunk(
    "termTemplates/fetchAllTermTemplates",
    async (filters: { beneficiaryId?: number | null; includeGeneric?: boolean } | undefined, { rejectWithValue }) => {
        try {
            const params: Record<string, string> = {};
            if (filters?.beneficiaryId === null) params.beneficiaryId = "null";
            else if (typeof filters?.beneficiaryId === "number") params.beneficiaryId = String(filters.beneficiaryId);
            if (filters?.includeGeneric) params.includeGeneric = "true";

            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/template`, { params });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Fetches framework contracts (term_templates with beneficiaryId) for a specific beneficiary
export const fetchBeneficiaryFrameworkContracts = createAsyncThunk(
    "termTemplates/fetchBeneficiaryFrameworkContracts",
    async ({ beneficiaryId }: { beneficiaryId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/template`, {
                params: { beneficiaryId: String(beneficiaryId) },
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get one specific template
export const fetchTermTemplate = createAsyncThunk(
    "termTemplates/fetchTermTemplate",
    async ({ templateId }: { templateId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/template/${templateId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const createTermTemplate = createAsyncThunk(
    "termTemplates/createTermTemplate",
    async (
        { data }: { data: Omit<TermTemplate, "id" | "modifiedAt" | "lastModifiedBy" | "createdBy"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/term/template`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateTermTemplate = createAsyncThunk(
    "termTemplate/updateTermTemplate",
    async (
        {
            templateId,
            data,
        }: { templateId: number; data: Omit<TermTemplate, "id" | "modifiedAt" | "createdBy" | "lastModifiedBy"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/term/template/${templateId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteTermTemplate = createAsyncThunk(
    "termTemplate/deleteTermTemplate",
    async ({ templateId }: { templateId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/term/template/${templateId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface TermTemplate {
    /**
     * Unique identifier
     */
    id: number;
    /**
     * Company it belongs to
     */
    companyId: number;
    /**
     * User ID who created the template
     */
    createdBy: number;
    /**
     * Name of the template
     */
    name: string;
    /**
     * Configuration in JSON format
     */
    configuration: TemplateConfiguration;
    /**
     * Last modification date
     */
    modifiedAt: Date;
    /**
     * Creation date (nullable for legacy rows created before the column existed)
     */
    createdAt?: Date | null;
    /**
     * Last user who operated changes
     */
    lastModifiedBy: number;
    /**
     * When set, the template is a framework contract (contrat cadre) attached to this beneficiary.
     * When null, it's a generic term template (typologie).
     */
    beneficiaryId?: number | null;
}

export interface TemplateConfiguration {
    advancePayment: number;
    channels: ChannelParams[];
    compensate: number;
    startDate?: string | null;
    endDate?: string | null;
}

/**
 * Press / audiovisual variant. Company is mono-mode (setting-driven), so channel
 * is implicit — only the price and the work nature are worth persisting.
 */
export interface PressTemplateConfiguration {
    folioPrice: number;
    workNature: string;
    startDate?: string | null;
    endDate?: string | null;
}

interface InitialState {
    termTemplate: TermTemplate;
    termTemplateStatus: TFetchStatus;
    termTemplates: TermTemplate[];
    templatesStatus: TFetchStatus;
    beneficiaryFrameworkContracts: TermTemplate[];
    beneficiaryFrameworkContractsStatus: TFetchStatus;
}

const initialState: InitialState = {
    termTemplate: {} as TermTemplate,
    termTemplateStatus: "idle",
    termTemplates: [],
    templatesStatus: "idle",
    beneficiaryFrameworkContracts: [],
    beneficiaryFrameworkContractsStatus: "idle",
};

export const termTemplatesSlice = createSlice({
    name: "termTemplates",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchAllTermTemplates.fulfilled, (state, { payload }) => {
            state.termTemplates = payload;
            state.templatesStatus = "succeeded";
        });
        builder.addCase(fetchAllTermTemplates.pending, (state, { payload }) => {
            state.templatesStatus = "pending";
        });
        builder.addCase(fetchAllTermTemplates.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.templatesStatus = "failed";
        });
        builder.addCase(fetchTermTemplate.pending, (state) => {
            state.termTemplateStatus = "pending";
        });
        builder.addCase(fetchTermTemplate.fulfilled, (state, { payload }) => {
            state.termTemplate = payload;
            state.termTemplateStatus = "succeeded";
        });
        builder.addCase(fetchTermTemplate.rejected, (state, { payload }) => {
            state.termTemplateStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchBeneficiaryFrameworkContracts.fulfilled, (state, { payload }) => {
            state.beneficiaryFrameworkContracts = payload;
            state.beneficiaryFrameworkContractsStatus = "succeeded";
        });
        builder.addCase(fetchBeneficiaryFrameworkContracts.pending, (state) => {
            state.beneficiaryFrameworkContractsStatus = "pending";
        });
        builder.addCase(fetchBeneficiaryFrameworkContracts.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.beneficiaryFrameworkContractsStatus = "failed";
        });
        builder.addCase(createTermTemplate.fulfilled, (state, { payload }) => {
            const data = { ...payload, modifiedAt: new Date() };
            if (data.beneficiaryId) {
                state.beneficiaryFrameworkContracts = [...state.beneficiaryFrameworkContracts, data];
                toast.success(`${t("TermTemplates.FrameworkCreateSuccess")}`);
            } else {
                state.termTemplates = [...state.termTemplates, data];
                toast.success(`${t("TermTemplates.CreateSuccess")}`);
            }
        });
        builder.addCase(createTermTemplate.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(updateTermTemplate.fulfilled, (state, { payload }) => {
            state.termTemplates = state.termTemplates.map((template) =>
                template.id === payload.id ? payload : template,
            );
            state.beneficiaryFrameworkContracts = state.beneficiaryFrameworkContracts.map((template) =>
                template.id === payload.id ? payload : template,
            );
            toast.success(
                `${t(payload.beneficiaryId ? "TermTemplates.FrameworkEditSuccess" : "TermTemplates.EditSuccess")}`,
            );
        });
        builder.addCase(updateTermTemplate.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteTermTemplate.fulfilled, (state, action) => {
            const wasFramework = state.beneficiaryFrameworkContracts.some(
                (template) => template.id === action.meta.arg.templateId,
            );
            state.termTemplates = state.termTemplates.filter((template) => template.id !== action.meta.arg.templateId);
            state.beneficiaryFrameworkContracts = state.beneficiaryFrameworkContracts.filter(
                (template) => template.id !== action.meta.arg.templateId,
            );
            toast.success(
                `${t(wasFramework ? "TermTemplates.FrameworkDeleteSuccess" : "TermTemplates.DeleteSuccess")}`,
            );
        });
        builder.addCase(deleteTermTemplate.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default termTemplatesSlice.reducer;
